import { Box, Text } from '@ltvco/refresh-lib/theme';
import { PersonOutline } from '@mui/icons-material';

import { ATTORNEY_ROLES, ATTORNEY_ROLE_DISPLAY_MAP } from '../../../constants';
import {
  InlineLoading,
  ErrorStateContainer,
  EmptyStateContainer,
  PartiesSection,
} from '../../../shared';

import {
  PartiesAttorneysContainer,
  SectionTitle,
  PartiesGrid,
  PartyCard,
  PartyHeader,
  PartyNameRow,
  PartyIconContainer,
  PartyName,
  PartyExtraInfo,
  Divider,
  AttorneySection,
  RepresentedByLabel,
  AttorneyInfoContainer,
  AttorneyName,
  AttorneyAddress,
  AttorneyPhone,
  AttorneyRoleText,
  NoAttorneyText,
} from './components';

import type { Attorney, Party, PartiesAttorneysResult } from '../../../types';

interface PartiesAttorneysProps {
  partiesAttorneysResult: PartiesAttorneysResult;
}

export const PartiesAttorneys = ({
  partiesAttorneysResult,
}: PartiesAttorneysProps) => {
  const { attorneysData, partiesData, isLoading, hasError, error } =
    partiesAttorneysResult;

  if (isLoading) {
    return <InlineLoading />;
  }

  if (hasError) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Failed to load parties and attorneys data';
    return (
      <ErrorStateContainer>
        <Text variant="body1" color="error">
          Error loading parties and attorneys data: {errorMessage}
        </Text>
      </ErrorStateContainer>
    );
  }

  const groupPartiesByType = (parties: Party[]) => {
    const grouped: { [key: string]: Party[] } = {};

    parties.forEach((party) => {
      const primaryPartyType = party.party_types?.[0]?.name || 'Other';
      if (!grouped[primaryPartyType]) {
        grouped[primaryPartyType] = [];
      }
      grouped[primaryPartyType].push(party);
    });

    Object.keys(grouped).forEach((partyType) => {
      grouped[partyType].sort((a, b) => {
        const nameA = a.name?.toLowerCase() || '';
        const nameB = b.name?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
      });
    });

    return grouped;
  };

  const getAttorneysForParty = (party: Party): Attorney[] => {
    if (!attorneysData.length || !party.attorneys) {
      return [];
    }

    const attorneyIds = party.attorneys.map((a) => a.attorney_id);
    const matchingAttorneys = attorneysData.filter((attorney) =>
      attorneyIds.includes(attorney.id)
    );

    return matchingAttorneys.sort((a, b) => {
      const nameA = a.name?.toLowerCase() || '';
      const nameB = b.name?.toLowerCase() || '';
      return nameA.localeCompare(nameB);
    });
  };

  const formatPhoneNumber = (phone: string): string => {
    const digits = phone.replace(/\D/g, '');

    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }

    return phone;
  };

  const parseAttorneyContact = (attorney: Attorney) => {
    const contact = attorney.contact_raw || '';
    const result = {
      name: attorney.name || '',
      phone: attorney.phone ? formatPhoneNumber(attorney.phone) : '',
      office: '',
      addressLines: [] as string[],
    };

    if (!contact) {
      return result;
    }

    const lines = contact.split('\n').filter(Boolean);
    const phoneRegex = /(\d{3}[-.\s]?\d{3}[-.\s]?\d{4})/;
    const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;

    // First line is typically the attorney name if not already set
    if (!result.name && lines.length > 0) {
      const firstLine = lines[0].trim();
      // Check if first line looks like a name (not a phone, email, or address pattern)
      if (
        !phoneRegex.test(firstLine) &&
        !emailRegex.test(firstLine) &&
        !firstLine.match(/^\d+/) // Doesn't start with numbers (address)
      ) {
        result.name = firstLine;
      }
    }

    // Extract phone number
    for (const line of lines) {
      const phoneMatch = line.match(phoneRegex);
      if (phoneMatch && !result.phone) {
        result.phone = formatPhoneNumber(phoneMatch[1]);
      }
    }

    // Extract office/firm name and address lines
    let foundOffice = false;
    for (const line of lines) {
      const trimmedLine = line.trim();
      const lowerLine = trimmedLine.toLowerCase();

      // Skip if it's the name, phone, email, fax, or empty
      if (
        trimmedLine === result.name ||
        phoneRegex.test(trimmedLine) ||
        emailRegex.test(trimmedLine) ||
        lowerLine.includes('fax') ||
        lowerLine.includes('phone') ||
        !trimmedLine
      ) {
        continue;
      }

      // Office name is typically the first non-name, non-phone line
      // It usually doesn't look like an address (no numbers at start, shorter)
      if (!foundOffice && !trimmedLine.match(/^\d+/)) {
        // Check if it looks like an office name (not too long, no typical address patterns)
        if (
          trimmedLine.length < 100 &&
          !trimmedLine.match(/,\s*[A-Z]{2}\s+\d{5}/)
        ) {
          result.office = trimmedLine;
          foundOffice = true;
          continue;
        }
      }

      // Everything else is an address line
      result.addressLines.push(trimmedLine);
    }

    return result;
  };

  const getPartyTypeExtraInfo = (party: Party): string => {
    return party.party_types?.[0]?.extra_info || party.extra_info || '';
  };

  const getAttorneyRoleDisplays = (
    attorney: Attorney,
    party: Party
  ): string[] => {
    if (!party.attorneys || party.attorneys.length === 0) {
      return [ATTORNEY_ROLE_DISPLAY_MAP[ATTORNEY_ROLES.ATTORNEY_TO_BE_NOTICED]];
    }

    const attorneyRoles = party.attorneys
      .filter((a) => a.attorney_id === attorney.id)
      .map((a) => a.role);

    if (attorneyRoles.length === 0) {
      return [ATTORNEY_ROLE_DISPLAY_MAP[ATTORNEY_ROLES.ATTORNEY_TO_BE_NOTICED]];
    }

    if (attorneyRoles.includes(ATTORNEY_ROLES.PRO_SE)) {
      return [ATTORNEY_ROLE_DISPLAY_MAP[ATTORNEY_ROLES.PRO_SE]];
    }

    const roleDisplayTexts: string[] = [];

    if (
      attorneyRoles.includes(ATTORNEY_ROLES.LEAD_ATTORNEY) ||
      attorneyRoles.includes(ATTORNEY_ROLES.SPECIAL_ROLE)
    ) {
      roleDisplayTexts.push(
        ATTORNEY_ROLE_DISPLAY_MAP[ATTORNEY_ROLES.LEAD_ATTORNEY]
      );
    }

    if (attorneyRoles.includes(ATTORNEY_ROLES.ATTORNEY_TO_BE_NOTICED)) {
      roleDisplayTexts.push(
        ATTORNEY_ROLE_DISPLAY_MAP[ATTORNEY_ROLES.ATTORNEY_TO_BE_NOTICED]
      );
    }

    return roleDisplayTexts.length > 0
      ? roleDisplayTexts
      : [ATTORNEY_ROLE_DISPLAY_MAP[ATTORNEY_ROLES.ATTORNEY_TO_BE_NOTICED]];
  };

  const partiesByType =
    partiesData.length > 0 ? groupPartiesByType(partiesData) : {};

  const sortedPartyTypes = Object.keys(partiesByType).sort((a, b) => {
    return a.localeCompare(b);
  });

  if (sortedPartyTypes.length === 0) {
    return (
      <EmptyStateContainer>
        <Text variant="body1" color="text.secondary">
          No parties and attorneys data available
        </Text>
      </EmptyStateContainer>
    );
  }

  return (
    <PartiesAttorneysContainer>
      {sortedPartyTypes.map((partyType, typeIndex) => {
        const parties = partiesByType[partyType];
        const isLastSection = typeIndex === sortedPartyTypes.length - 1;

        return (
          <PartiesSection key={partyType} sx={{ mb: isLastSection ? 0 : 3 }}>
            <SectionTitle>{partyType}</SectionTitle>
            <PartiesGrid sx={{ mt: 0 }}>
              {parties.map((party: Party) => {
                const partyAttorneys = getAttorneysForParty(party);
                const extraInfo = getPartyTypeExtraInfo(party);

                return (
                  <PartyCard key={party.id}>
                    <PartyHeader>
                      <PartyNameRow>
                        <PartyIconContainer>
                          <PersonOutline />
                        </PartyIconContainer>
                        <PartyName>{party.name}</PartyName>
                      </PartyNameRow>
                      {extraInfo && (
                        <PartyExtraInfo>{extraInfo}</PartyExtraInfo>
                      )}
                    </PartyHeader>

                    <Divider />

                    <AttorneySection>
                      {party.attorneys && party.attorneys.length > 0 ? (
                        <>
                          <RepresentedByLabel>
                            Represented by:
                          </RepresentedByLabel>
                          {partyAttorneys.map((attorney: Attorney) => {
                            const contactInfo = parseAttorneyContact(attorney);
                            const roleDisplays = getAttorneyRoleDisplays(
                              attorney,
                              party
                            );

                            return (
                              <Box key={attorney.id}>
                                <AttorneyInfoContainer>
                                  {/* Attorney name - primary color */}
                                  {contactInfo.name && (
                                    <AttorneyName>
                                      {contactInfo.name}
                                    </AttorneyName>
                                  )}

                                  {/* Phone number - secondary color, formatted */}
                                  {contactInfo.phone && (
                                    <AttorneyPhone>
                                      {contactInfo.phone}
                                    </AttorneyPhone>
                                  )}

                                  {/* Office/firm name - secondary color */}
                                  {contactInfo.office && (
                                    <AttorneyAddress>
                                      {contactInfo.office}
                                    </AttorneyAddress>
                                  )}

                                  {/* Address lines - secondary color */}
                                  {contactInfo.addressLines.map(
                                    (line: string, lineIndex: number) => (
                                      <AttorneyAddress key={lineIndex}>
                                        {line}
                                      </AttorneyAddress>
                                    )
                                  )}
                                </AttorneyInfoContainer>
                                {roleDisplays.length > 0 && (
                                  <AttorneyRoleText sx={{ mt: 1 }}>
                                    {roleDisplays.join(' ')}
                                  </AttorneyRoleText>
                                )}
                              </Box>
                            );
                          })}
                        </>
                      ) : (
                        <>
                          <RepresentedByLabel>
                            Represented by:
                          </RepresentedByLabel>
                          <NoAttorneyText>No attorney assigned</NoAttorneyText>
                        </>
                      )}
                    </AttorneySection>
                  </PartyCard>
                );
              })}
            </PartiesGrid>
          </PartiesSection>
        );
      })}
    </PartiesAttorneysContainer>
  );
};
